/**
 * Guidance for the page the user is on (SEITEN-HINWEIS).
 *
 * This was a static list of every page inside the cached system prompt, re-read on every
 * model step although only the current page's entry can apply. Like
 * `buildSellerRefreshContext`, it rides in the per-conversation part now, and only for a page
 * it knows; any other page gets nothing and the general rules apply.
 *
 * `/angebote/<id>` and `/anfragen/<id>` are the owner's views (`app/(user-area)`), so they share
 * the list's entry: the static list described the first as a buyer weighing a purchase and the
 * second as the market from a seller's side.
 */

import { routes } from "@/lib/routes";

/**
 * The path of the page the chat runs on. app/api/chat/route.ts sends `${origin}${pathname}`,
 * tests and evals the bare path; every reader of `currentUrl` goes through here, so the three
 * cannot disagree about the same value again (the "Aktuelle Seite" line once read only the
 * bare path and never reached production).
 */
export function pagePathOf(url: string): string | null {
  try {
    return new URL(url, "https://page.invalid").pathname;
  } catch {
    return null;
  }
}

const under = (base: string) => (path: string) => path === base || path.startsWith(`${base}/`);
/** `/schnellangebot/standort` → `/schnellangebot`: every step of the funnel. */
const funnelOf = (entry: string) => `/${entry.split("/")[1]}`;

const PAGE_HINTS: { matches: (path: string) => boolean; hint: string }[] = [
  {
    matches: (path) => path === routes.home,
    hint: "Startseite: Der Nutzer orientiert sich. Ist sein Anliegen noch offen, frage, ob er kaufen, verkaufen oder sich allgemein informieren möchte, und biete an, Angebote oder Anfragen direkt im Chat aufzunehmen.",
  },
  {
    matches: under(routes.offerings.list),
    hint: "Meine Angebote: Der Nutzer verwaltet hier seine eigenen Angebote (erfordert Login); die Seite zeigt NICHT den öffentlichen Markt. Hilf bei Fragen zu eigenen Angeboten, Status und Bearbeitung.",
  },
  {
    matches: under(routes.requests.list),
    hint: "Meine Anfragen: Der Nutzer verwaltet hier seine eigenen Anfragen (erfordert Login); die Seite zeigt NICHT den öffentlichen Markt. Hilf bei Fragen zu eigenen Anfragen, Status und Bearbeitung.",
  },
  {
    matches: (path) => path === routes.content.preisindex,
    hint: `Öffentliche Preisübersicht mit interaktiver Karte. Bei Fragen nach aktuellen Preisen gib den exakten Pfad ${routes.content.preisindex} wörtlich im Text aus (anklickbarer Link), nicht nur eine Umschreibung.`,
  },
  {
    matches: under(funnelOf(routes.schnellangebot)),
    hint: 'Schnellangebot: Der Nutzer will VERKAUFEN und füllt das Angebotsformular aus. Hilf aktiv bei den Formularfeldern: was angeboten wird (Biotopausgleich, Erstaufforstungsfläche, Artenschutz, Sonderbiotop), Bundesland, Naturraum (nutze "get_naturraum_info" bei PLZ), beim Biotopausgleich Preis und Menge IN SEINER EINHEIT (Ökopunkte ⇒ €/Punkt, Ausgleichsfläche ⇒ €/ha). Beantworte Fragen zu den einzelnen Feldern. Wenn der Nutzer unsicher beim Preis ist, nutze "get_market_stats" für aktuelle Marktdaten.\n→ Für eingeloggte Nutzer: Biete an, den Entwurf direkt über "create_offering_draft" zu starten, wenn sie den Chat dem Formular vorziehen.',
  },
  {
    matches: under(funnelOf(routes.schnellanfrage)),
    hint: 'Schnellanfrage: Der Nutzer will KAUFEN und füllt das Anfrageformular aus. Hilf bei Formularfeldern: benötigte Menge, gewünschtes Bundesland, maximaler Preis pro Punkt, Kompensationsinstrument. Erkläre den Matching-Prozess: Das System findet automatisch passende Angebote.\n→ Für eingeloggte Nutzer: Biete an, die Anfrage direkt über "create_request_draft" zu starten, wenn sie den Chat dem Formular vorziehen.',
  },
  {
    matches: under(routes.deals.list),
    hint: "Deals: Der Nutzer verwaltet laufende Verhandlungen. Erkläre den Deal-Prozess: Angebot → Verhandlung → Vertrag → Abschluss. Biete einen Rückruf an, wenn rechtliche oder vertragliche Fragen bestehen.",
  },
  {
    matches: (path) => path === routes.profile,
    hint: "Profil: Der Nutzer verwaltet sein Konto. Hilf bei Fragen zu Einstellungen, eigenen Angeboten und Anfragen.",
  },
];

/** The SEITEN-HINWEIS block for `currentUrl`, or "" when no entry applies. */
export function buildPageContext(currentUrl?: string): string {
  const path = currentUrl ? pagePathOf(currentUrl) : null;
  const page = path ? PAGE_HINTS.find((entry) => entry.matches(path)) : undefined;
  return page ? `SEITEN-HINWEIS:\n${page.hint}` : "";
}
